import { Fragment } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { deleteContactAddress, updateContactAddress } from 'api/contacts/single';
import { PrimaryBadge } from 'assets/js/pages/contacts/PrimaryBadge';
import * as AddressManagement from 'components/address-form/AddressManagement';
import * as DropdownMenu from 'components/dropdown-menu';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { capitalize } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';

const ContactAddresses = () => {
    const toast = useToast();
    const history = useHistory();
    const match = useRouteMatch();
    const { data: contact } = useCurrentContact();
    const queryClient = useQueryClient();
    const contactId = contact.id;
    const { accountId } = useCurrentAccount();

    const { mutate: makePrimary } = useMutation({
        mutationFn: (addressId: number) =>
            updateContactAddress(accountId, contactId, addressId, { isPrimary: true }),
        onSuccess: () => {
            toast.success(`Updated primary address successfully!`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contactId),
            });
        },
    });

    const { mutate: deleteAddress } = useMutation({
        mutationFn: (addressId: number) => deleteContactAddress(accountId, contactId, addressId),
        onSuccess: () => {
            toast.success(`Deleted address successfully!`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contactId),
            });
        },
    });

    return (
        <AddressManagement.Root className="gap-0 p-0">
            <AddressManagement.Row className={'flex items-center justify-between py-3 pl-6 pr-4'}>
                <AddressManagement.Title>Addresses</AddressManagement.Title>
                <AddressManagement.Button
                    onClick={() => history.push(`${match.url}/addresses/new`)}
                />
            </AddressManagement.Row>
            <Separator />
            <div className={'flex flex-col gap-3 px-6 py-3'}>
                {contact.addresses.length > 0 ? (
                    contact.addresses.map((address, index) => (
                        <Fragment key={address.id}>
                            {index > 0 && <Separator className={'-mx-6 !w-auto'} />}
                            <AddressManagement.Row>
                                <AddressManagement.RowDetails>
                                    <p className={'text-sm text-gray-600'}>
                                        {capitalize(address.type)}
                                    </p>
                                    <AddressManagement.InlineAddressRoot address={address}>
                                        <AddressManagement.InlineAddressCopyButton />
                                        {address.is_primary && <PrimaryBadge />}
                                    </AddressManagement.InlineAddressRoot>
                                </AddressManagement.RowDetails>
                                <DropdownMenu.Root>
                                    <DropdownMenu.EllipsisTrigger />
                                    <DropdownMenu.Content align={'end'}>
                                        {!address.is_primary && (
                                            <DropdownMenu.Item
                                                onClick={() => makePrimary(address.id)}
                                            >
                                                Make Primary
                                            </DropdownMenu.Item>
                                        )}
                                        <DropdownMenu.Item
                                            onClick={() =>
                                                history.push(
                                                    `${match.url}/addresses/${address.id}/edit`,
                                                )
                                            }
                                        >
                                            Edit
                                        </DropdownMenu.Item>
                                        <AlertDialog.Root type={'error'}>
                                            <AlertDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    Delete
                                                </DropdownMenu.Item>
                                            </AlertDialog.Trigger>
                                            <AlertDialog.Portal>
                                                <AlertDialog.Overlay />
                                                <AlertDialog.Content>
                                                    <AlertDialog.Header>
                                                        <AlertDialog.Icon />
                                                        <AlertDialog.Close />
                                                    </AlertDialog.Header>
                                                    <AlertDialog.Body>
                                                        <AlertDialog.Title>
                                                            Delete address
                                                        </AlertDialog.Title>
                                                        <AlertDialog.Description>
                                                            You won't be able to undo this action.
                                                            Are you sure?
                                                        </AlertDialog.Description>
                                                    </AlertDialog.Body>
                                                    <AlertDialog.Footer>
                                                        <AlertDialog.Cancel>
                                                            Cancel
                                                        </AlertDialog.Cancel>
                                                        <AlertDialog.Action
                                                            onClick={() =>
                                                                deleteAddress(address.id)
                                                            }
                                                        >
                                                            Delete Address
                                                        </AlertDialog.Action>
                                                    </AlertDialog.Footer>
                                                </AlertDialog.Content>
                                            </AlertDialog.Portal>
                                        </AlertDialog.Root>
                                    </DropdownMenu.Content>
                                </DropdownMenu.Root>
                            </AddressManagement.Row>
                        </Fragment>
                    ))
                ) : (
                    <p className={'p-4 text-center text-gray-600'}>No addresses found</p>
                )}
            </div>
        </AddressManagement.Root>
    );
};

export { ContactAddresses };
